import * as React from "react";
import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  TooltipProps,
  styled,
  Tooltip,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  GlobalMonitoringStatus,
  ItemMonitoringStatus,
  RealTimeItemMonitoring,
  RealTimeProgressBar,
  RealTimeToggleButton,
} from "../type";
import { QuestionMark } from "@mui/icons-material";
import Typography from "@common/components/Typography";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

type RealTimeIconContainerProps = {
  status?: ItemMonitoringStatus;
  type: RealTimeItemMonitoring["type"];
  globalStatus: GlobalMonitoringStatus;
  tooltip?: TooltipProps["title"];
  onClick?: () => void;
  disabled?: boolean;
  progressbarData?: RealTimeProgressBar;
  dataTestId?: string;
};

const StyledButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  "& > .MuiToggleButton-root": {
    height: "fit-content",
    borderTopWidth: "2px",
    borderBottomWidth: "2px",
    padding: `0 ${theme.spacing(1.5)}`,
    borderRadius: "12px",
    "&:not(.Mui-selected)": {
      borderColor: theme.palette.primary.main,
    },
    "&:first-of-type": { borderLeftWidth: "2px" },
    "&:last-of-type": { borderRightWidth: "2px" },
    "&.MuiToggleButtonGroup-grouped:not(:first-of-type)": {
      borderLeftColor: theme.palette.primary.main,
    },
  },
}));

const RealTimeIconContainer = (
  props: React.PropsWithChildren<RealTimeIconContainerProps>
) => {
  const theme = useTheme();
  const {
    status = "default",
    type,
    globalStatus,
    tooltip = "",
    onClick = () => {},
    disabled,
    progressbarData,
    dataTestId
  } = props;
  const { t } = useTranslation();

  let progress = 0;
  if (progressbarData) {
    progress =
      progressbarData.end === undefined
        ? 50
        : progressbarData.start === progressbarData.end
        ? 100
        : ((progressbarData.progress - progressbarData.start) /
            (progressbarData.end - progressbarData.start)) *
          100;
  }

  const GlobalStatusComputeStyle = useMemo(() => {
    switch (globalStatus) {
      case "paused":
        return {
          color: theme.palette.device.border,
          borderColor: theme.palette.device.border,
          "&.MuiButton-root": {
            backgroundColor: theme.palette.device.border,
            "&:hover": {
              backgroundColor: theme.palette.device.border,
            },
          },
        };
      case "meeting":
        return {
          color: theme.palette.meeting.background,
          borderColor: theme.palette.meeting.background,
          "&.MuiButton-root": {
            backgroundColor: theme.palette.meeting.background,
            color: theme.palette.common.white,
            "&:hover": {
              backgroundColor: theme.palette.meeting.dark,
            },
          },
          "&.MuiTypography-root": {
            color: theme.palette.common.white,
            backgroundColor: theme.palette.meeting.darker,
          },
          "&.Mui-disabled": {
            backgroundColor: theme.palette.meeting.darker,
            "& .MuiSvgIcon-root": {
              color: theme.palette.background.paper,
            },
          },
        };
      case "loading":
        return {
          color: theme.palette.common.white,
          borderColor: theme.palette.grey[500],
          animation: "$bgpulsating 2s infinite",
          "&.MuiButton-root": {
            animation: "none",
          },
        };
      default:
        return {
          color: theme.palette.primary.main,
          borderColor: theme.palette.primary.main,
          "&.MuiButton-root": {
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          },
        };
    }
  }, [globalStatus, theme]);

  const StatusComputeStyle = useMemo(() => {
    switch (status) {
      case "error":
        return {
          color: theme.palette.error.main,
          borderColor: theme.palette.error.main,
          "&.MuiButton-root": {
            backgroundColor: theme.palette.error.main,
            "&:hover": {
              backgroundColor: theme.palette.error.dark,
            },
          },
          "&.Mui-selected": {
            color: theme.palette.common.black,
            backgroundColor: theme.palette.error.main,
            "&:hover": {
              backgroundColor: theme.palette.error.dark,
            },
          },
        };
      case "success":
        return {
          color: theme.palette.success.main,
          borderColor: theme.palette.success.main,
          "&.MuiButton-root": {
            backgroundColor: theme.palette.success.main,
            "&:hover": {
              backgroundColor: theme.palette.success.dark,
            },
          },
          "&.Mui-selected": {
            color: theme.palette.common.black,
            backgroundColor: theme.palette.success.main,
            "&:hover": {
              backgroundColor: theme.palette.success.dark,
            },
          },
        };
      case "paused":
        return {
          color: theme.palette.device.border,
          borderColor: theme.palette.device.border,
          "&.MuiButton-root": {
            backgroundColor: theme.palette.device.border,
            "&:hover": {
              backgroundColor: theme.palette.device.border,
            },
          },
        };
      case "troubleshooting":
        return {
          color: theme.palette.error.main,
          borderColor: theme.palette.error.main,
          "&.MuiButton-root": {
            backgroundColor: theme.palette.error.main,
            "&:hover": {
              backgroundColor: theme.palette.error.dark,
            },
          },
        };
      case "loading":
        return {
          color: theme.palette.common.white,
          borderColor: theme.palette.grey[500],
          animation: "$bgpulsating 2s infinite",
          "&.MuiButton-root": {
            animation: "none",
          },
        };
      case "unknown":
        return {
          color: theme.palette.common.grey.g9B9B9B,
          borderColor: theme.palette.grey[500],
          "&.MuiButton-root": {
            backgroundColor: theme.palette.grey[500],
            "&:hover": {
              backgroundColor: theme.palette.grey[600],
            },
          },
          "&.Mui-selected": {
            color: theme.palette.common.black,
            backgroundColor: theme.palette.common.grey.g9B9B9B,
            "&:hover": {
              backgroundColor: theme.palette.grey[600],
            },
          },
        };
      case "enabledPaused":
        return {
          color: theme.palette.device.label,
          borderColor: theme.palette.device.label,
          "&.MuiButton-root": {
            backgroundColor: theme.palette.device.label,
            "&:hover": {
              backgroundColor: theme.palette.primary.main,
            },
          },
        };
      default:
        return {
          color: theme.palette.primary.main,
          borderColor: theme.palette.primary.main,
          "&.MuiButton-root": {
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          },
        };
    }
  }, [status, theme]);

  return type === "button" ? (
    <Tooltip title={tooltip} arrow>
      <span>
        <Button
          variant={"contained"}
          sx={[
            {
              "&.MuiButton-root": {
                width: "40px",
                height: "40px",
                borderWidth: "2px",
                borderStyle: "solid",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minWidth: "inherit",
              },
              "&.MuiButton-contained": {
                border: "none",
              },
              "& .MuiSvgIcon-root": {
                color: theme.palette.common.black,
              },
            },
            GlobalStatusComputeStyle,
            status !== "default" && StatusComputeStyle,
          ]}
          onClick={onClick}
          disabled={
            (disabled !== undefined && disabled) ||
            (disabled === undefined &&
              (globalStatus === "loading" || globalStatus === "paused"))
          }
          data-testid={dataTestId}
        >
          {props.children}
          <Box
            sx={({ palette }) => ({
              ...(status === "troubleshooting"
                ? {
                    backgroundImage: `conic-gradient(transparent 60deg,${palette.error.dark} 120deg)`,
                    height: "3rem",
                    width: "3rem",
                    borderRadius: "1.5rem",
                    position: "absolute",
                    animation: "rotate 2s infinite linear",
                    zIndex: "-1",
                    "@keyframes rotate": {
                      "100%": {
                        transform: "rotate(360deg)",
                      },
                    },
                  }
                : {}),
            })}
          />
        </Button>
      </span>
    </Tooltip>
  ) : type === "indication" ? (
    <Tooltip title={tooltip} arrow>
      <Box
        sx={[
          {
            width: "40px",
            height: "40px",
            borderWidth: "2px",
            borderStyle: "solid",
            borderRadius: "50%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minWidth: "inherit",
          },
          GlobalStatusComputeStyle,
          status !== "default" && StatusComputeStyle,
        ]}
      >
        {props.children}
      </Box>
    </Tooltip>
  ) : type === "togglebuttons" ? (
    globalStatus === "loading" || globalStatus === "paused" ? (
      <Box
        sx={[
          {
            width: "40px",
            height: "40px",
            borderWidth: "2px",
            borderStyle: "solid",
            borderRadius: "50%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minWidth: "inherit",
          },
          GlobalStatusComputeStyle,
        ]}
        data-testid={dataTestId}
      >
        <QuestionMark />
      </Box>
    ) : (
      <Tooltip title={tooltip} arrow>
        <StyledButtonGroup>
          {(props.children as RealTimeToggleButton[]).map(
            (button, index) =>
              Number(button.label) > 0 && (
                <ToggleButton
                  sx={{
                    color: (theme) => {
                      switch (button.color) {
                        case "success":
                          return {
                            color: theme.palette.success.main,
                          };
                        case "error":
                          return {
                            color: theme.palette.error.main,
                          };
                        default:
                          return {
                            color: theme.palette.common.grey.g9B9B9B,
                          };
                      }
                    },
                  }}
                  value={button.color}
                  onClick={() => button.onClick()}
                  key={`toggleBtnRTIC${button.label}_${index}`}
                  selected={button.selected}
                >
                  {Number(button.label) > 99 ? "99+" : button.label}
                </ToggleButton>
              )
          )}
        </StyledButtonGroup>
      </Tooltip>
    )
  ) : type === "label" ? (
    globalStatus !== "loading" && props.children ? (
      <Typography
        sx={[
          {
            borderRadius: "10rem",
            padding: (theme) => `0 ${theme.spacing(4)}`,
            minHeight: "1.5rem",
            textAlign: "center",
            width: "100%",
          },
          GlobalStatusComputeStyle,
        ]}
        size="medium"
      >
        {props.children}
      </Typography>
    ) : (
      <></>
    )
  ) : type === "progressbar" && progressbarData ? (
    <Grid
      container
      sx={{
        justifyContent: "space-between",
        borderRadius: "10rem",
        padding: (theme) => `0 ${theme.spacing(4)}`,
        width: "100%",

        //Straight progressbar
        //   `radial-gradient(circle at calc(${progress}% - 20px) 50%, ${palette.meeting.background} 20px, transparent 0),
        // linear-gradient(to right, ${palette.meeting.background} calc(${progress}% - 20px), ${palette.meeting.darker} calc(${progress}% - 20px))`, //Rounded progressbar
        background: ({ palette }) =>
          `linear-gradient(to right, ${palette.meeting.background} ${progress}%, ${palette.meeting.darker} ${progress}%)`,
      }}
    >
      <Grid>
        <Typography
          sx={{ color: (theme) => theme.palette.meeting.label }}
          size="smaller"
        >
          {progressbarData.startLabel}
        </Typography>
        <Typography realTime>
          {new Date(progressbarData.start).toLocaleTimeString("en-US", {
            timeStyle: "short",
          })}
        </Typography>
      </Grid>
      <Grid
        sx={{
          textAlign: "end",
        }}
      >
        <Typography
          sx={{ color: (theme) => theme.palette.meeting.label }}
          size="smaller"
        >
          {progressbarData.endLabel}
        </Typography>
        <Typography realTime>
          {progressbarData.end
            ? new Date(progressbarData.end).toLocaleTimeString("en-US", {
                timeStyle: "short",
              })
            : t("Not set up")}
        </Typography>
      </Grid>
    </Grid>
  ) : (
    <></>
  );
};

export default RealTimeIconContainer;
